import { supabase } from './supabaseClient';
import { Database } from '../../types/supabase';

type Lead = Database['public']['Tables']['leads']['Row'];
type LeadInsert = Database['public']['Tables']['leads']['Insert'];
type LeadUpdate = Database['public']['Tables']['leads']['Update'];

/**
 * Récupère tous les leads, avec filtre de campagne optionnel
 */
export const getLeads = async (campaignId?: string, userId?: string): Promise<{ data: Lead[] | null; error: Error | null }> => {
  let query = supabase.from('leads').select('*');
  
  if (campaignId) {
    query = query.eq('campaign_id', campaignId);
  }
  
  if (userId) {
    query = query.eq('user_id', userId);
  }
  
  const { data, error } = await query.order('created_at', { ascending: false });
  return { data, error: error as Error | null };
};

/**
 * Récupère tous les leads des campagnes de l'utilisateur connecté
 */
export const getLeadsByUserId = async (userId: string): Promise<{ data: Lead[] | null; error: Error | null }> => {
  // On peut maintenant récupérer directement les leads par user_id
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
    
  return { data, error: error as Error | null };
};

/**
 * Récupère un lead par son ID
 */
export const getLeadById = async (id: string): Promise<{ data: Lead | null; error: Error | null }> => {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('id', id)
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Crée un nouveau lead
 */
export const createLead = async (lead: LeadInsert, userId: string): Promise<{ data: Lead | null; error: Error | null }> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('leads')
    .insert({
      ...lead,
      user_id: userId,
      created_at: now,
      updated_at: now
    })
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour un lead existant
 */
export const updateLead = async (id: string, updates: LeadUpdate): Promise<{ data: Lead | null; error: Error | null }> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('leads')
    .update({
      ...updates,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Supprime un lead
 */
export const deleteLead = async (id: string): Promise<{ error: Error | null }> => {
  const { error } = await supabase
    .from('leads')
    .delete()
    .eq('id', id);
  
  return { error: error as Error | null };
};

/**
 * Récupère les leads qui ont un RDV (pour la page RDV)
 */
export const getLeadsWithRdv = async (userId?: string): Promise<{ data: Lead[] | null; error: Error | null }> => {
  let query = supabase
    .from('leads')
    .select('*')
    .eq('rdv', true);
  
  if (userId) {
    query = query.eq('user_id', userId);
  }
  
  const { data, error } = await query.order('rdv_date', { ascending: true });
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour le statut de qualification d'un RDV
 */
export const updateRdvStatus = async (
  id: string, 
  etatRdv: string,
  retourClient?: string,
  rdvCommentaire?: string
): Promise<{ data: Lead | null; error: Error | null }> => {
  const now = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('leads')
    .update({
      rdv_commentaire: rdvCommentaire || null,
      retour_client: retourClient || null,
      etat_rdv: etatRdv,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour l'étape de prospection d'un lead
 */
export const updateLeadProspectionStage = async (
  id: string, 
  stage: string
): Promise<{ data: Lead | null; error: Error | null }> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('leads')
    .update({
      prospection_etape: stage,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour le statut de qualification d'un lead (pas lié au RDV)
 */
export const updateLeadQualificationStatus = async (
  id: string, 
  qualified: boolean
): Promise<{ data: Lead | null; error: Error | null }> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('leads')
    .update({
      lead_qualifie: qualified,
      updated_at: now
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error: error as Error | null };
};

/**
 * Met à jour le statut de qualification de plusieurs leads
 */
export const updateMultipleLeadsQualificationStatus = async (
  ids: string[], 
  qualified: boolean
): Promise<{ data: Lead[] | null; error: Error | null }> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('leads')
    .update({
      lead_qualifie: qualified,
      updated_at: now
    })
    .in('id', ids)
    .select();
  
  return { data, error: error as Error | null };
}; 